import React, { useState } from 'react';
import { motion } from 'framer-motion';
import {
  Mail,
  Phone,
  MapPin,
  Clock,
  Send,
  CheckCircle,
  MessageSquare,
  User,
  Building,
  Globe,
  Award,
  Users,
  TrendingUp,
  ArrowRight,
  Facebook,
  Twitter,
  Instagram,
   Target, Star,
  Linkedin
} from 'lucide-react';
import ContactUsForm from '../ContactUsForm';


const ContactUs = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    subject: '',
    message: '',
    service: ''
  });
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({});

  const services = [
    'NGO Consultancy',
    'HR Solutions',
    'Research & Studies',
    'Technical Assistance',
    'Supply Chain Management',
    'Other'
  ];

  const contactInfo = [
    {
      icon: MapPin,
      title: 'Office Address',
      details: 'B-HUB, 5th Floor, Block-A, Maurya Lok Complex, Patna, Bihar - 800001',
      color: 'text-blue-600'
    },
    {
      icon: Phone,
      title: 'Phone Number',
      details: '+91-7979060329',
      color: 'text-green-600'
    },
    {
      icon: Mail,
      title: 'Email Address',
      details: 'info@lumconser.com',
      color: 'text-purple-600'
    },
    {
      icon: Clock,
      title: 'Business Hours',
      details: 'Mon - Fri: 9:00 AM - 6:00 PM',
      color: 'text-orange-600'
    }
  ];

  const socialLinks = [
    { icon: Facebook, href: 'https://www.facebook.com/lumconser', label: 'Facebook', color: 'hover:text-blue-600' },
   
    { icon: Linkedin, href: '#', label: 'LinkedIn', color: 'hover:text-blue-700' },
  ];

  const stats = [
 { icon: Users, value: "100+", label: "Happy Clients" },
    { icon: Award, value: "200+", label: "Projects Completed" },
    { icon: Target, value: "8+", label: "Years Experience" },
    { icon: Star, value: "98%", label: "Success Rate" },

    
  ];

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.name.trim()) {
      newErrors.name = 'Full name is required';
    } else if (formData.name.trim().length < 2) {
      newErrors.name = 'Name must be at least 2 characters';
    }
    
    if (!formData.email.trim()) {
      newErrors.email = 'Email address is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }
    
    if (formData.phone && !/^[\+]?[1-9][\d]{0,15}$/.test(formData.phone.replace(/\s/g, ''))) {
      newErrors.phone = 'Please enter a valid phone number';
    }
    
    if (!formData.subject.trim()) {
      newErrors.subject = 'Subject is required';
    } else if (formData.subject.trim().length < 5) {
      newErrors.subject = 'Subject must be at least 5 characters';
    }
    
    if (!formData.message.trim()) {
      newErrors.message = 'Message is required';
    } else if (formData.message.trim().length < 10) {
      newErrors.message = 'Message must be at least 10 characters';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setIsSubmitting(true);

    // Simulate form submission
    await new Promise(resolve => setTimeout(resolve, 2000));

    setIsSubmitted(true);
    setIsSubmitting(false);

    // Reset form after 3 seconds
    setTimeout(() => {
      setIsSubmitted(false);
      setFormData({
        name: '',
        email: '',
        phone: '',
        company: '',
        subject: '',
        message: '',
        service: ''
      });
      setErrors({});
    }, 3000);
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <div className="relative bg-gradient-to-br from-slate-800 via-slate-700 to-slate-900 text-white pt-24 sm:pt-32 pb-16 sm:pb-20 overflow-hidden">
        {/* Decorative elements */}
        <div className="absolute top-10 left-4 sm:left-10 w-16 h-16 sm:w-20 sm:h-20 bg-blue-400/20 rounded-full opacity-30 animate-pulse"></div>
        <div className="absolute top-20 right-4 sm:right-20 w-12 h-12 sm:w-16 sm:h-16 bg-cyan-400/20 rounded-full opacity-40 animate-pulse delay-1000"></div>
        <div className="absolute bottom-20 left-1/4 w-8 h-8 sm:w-12 sm:h-12 bg-purple-400/20 rounded-full opacity-50 animate-pulse delay-2000"></div>
        <div className="container-custom relative z-10 px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center"
          >
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4 sm:mb-6">Get In Touch</h1>
            <p className="text-lg sm:text-xl text-white mb-6 sm:mb-8 max-w-3xl mx-auto px-4">
              Ready to transform your business? Let's discuss how Lumbini Consultancy can help you achieve your goals. We're here to listen and provide expert guidance.
            </p>

            {/* Quick Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 sm:gap-6 mt-8 sm:mt-12">
              {stats.map((stat, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  className="text-center"
                >
                  <stat.icon className="h-6 w-6 sm:h-8 sm:w-8 mx-auto mb-2 text-cyan-400" />
                  <div className="text-white text-xl sm:text-2xl font-bold">{stat.value}</div>
                  <div className="text-white text-xs sm:text-sm font-medium">{stat.label}</div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </div>

 <ContactUsForm/>
      
    </div>
  );
};

export default ContactUs;